/**
 * TagPicker — selezione dei tag raggruppati per FACCETTA.
 *
 * È la resa visiva del principio anti-duplicato: i tag non sono un mucchio
 * unico, sono dimensioni indipendenti che si incrociano. Vedere le faccette
 * separate è ciò che spinge a combinare due tag esistenti invece di inventarne
 * uno nuovo che li fonde.
 *
 * Faccette e tag arrivano dai DATI: questo componente non conosce il dominio.
 */

import React, { useMemo } from 'react';
import { Box, Chip, Stack, Typography, alpha } from '@mui/material';
import { tokens } from '../../theme';
import { COLORE_FACCETTA_DEFAULT } from './labels';
import type { FaccettaRete, TagRete } from '../../types/reteContatti';

interface Props {
  faccette: FaccettaRete[];
  /** Tutti i tag disponibili (i `proposto` sono esclusi automaticamente). */
  tag: TagRete[];
  /** Slug dei tag attualmente selezionati. */
  selezionati: string[];
  onChange: (slugs: string[]) => void;
  /** Sola lettura: mostra i selezionati senza permettere modifiche. */
  readOnly?: boolean;
}

export const TagPicker: React.FC<Props> = ({
  faccette,
  tag,
  selezionati,
  onChange,
  readOnly = false,
}) => {
  // Solo i tag ATTIVI sono applicabili: i proposti aspettano l'approvazione.
  const attiviPerFaccetta = useMemo(() => {
    const out = new Map<string, TagRete[]>();
    for (const t of tag) {
      if (t.stato !== 'attivo') continue;
      const lista = out.get(t.faccetta) ?? [];
      lista.push(t);
      out.set(t.faccetta, lista);
    }
    for (const lista of out.values()) {
      lista.sort((a, b) => a.etichetta.localeCompare(b.etichetta, 'it', { sensitivity: 'base' }));
    }
    return out;
  }, [tag]);

  const toggle = (slug: string) => {
    if (readOnly) return;
    onChange(
      selezionati.includes(slug)
        ? selezionati.filter((s) => s !== slug)
        : [...selezionati, slug],
    );
  };

  return (
    <Stack spacing={2}>
      {faccette.map((f) => {
        const disponibili = attiviPerFaccetta.get(f.id) ?? [];
        // In sola lettura le faccette senza tag scelti non aggiungono nulla.
        if (readOnly && !disponibili.some((t) => selezionati.includes(t.id))) return null;
        const colore = f.colore || COLORE_FACCETTA_DEFAULT;

        return (
          <Box key={f.id}>
            <Typography
              sx={{
                fontSize: '0.62rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: tokens.color.mist,
                mb: 0.75,
              }}
            >
              {f.etichetta}
            </Typography>

            {disponibili.length === 0 ? (
              <Typography variant="caption" sx={{ color: tokens.color.mist, fontStyle: 'italic' }}>
                nessun tag ancora in questa faccetta
              </Typography>
            ) : (
              <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                {disponibili.map((t) => {
                  const attivo = selezionati.includes(t.id);
                  if (readOnly && !attivo) return null;
                  return (
                    <Chip
                      key={t.id}
                      label={t.etichetta}
                      size="small"
                      onClick={readOnly ? undefined : () => toggle(t.id)}
                      sx={{
                        // Touch target confortevole anche da tablet.
                        height: 30,
                        fontSize: '0.78rem',
                        fontWeight: attivo ? 600 : 400,
                        cursor: readOnly ? 'default' : 'pointer',
                        color: attivo ? colore : tokens.color.ash,
                        bgcolor: attivo ? alpha(colore, 0.14) : 'transparent',
                        border: `1px solid ${attivo ? alpha(colore, 0.45) : tokens.color.line}`,
                        '&:hover': {
                          bgcolor: readOnly ? undefined : alpha(colore, attivo ? 0.2 : 0.07),
                        },
                      }}
                    />
                  );
                })}
              </Stack>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};
